/**
 * Attractions domain config + pure helpers.
 *
 * Imported from BOTH server and client components, so this module must stay
 * free of `'use client'` and of any browser-only access at module scope
 * (`window`, `document`) — same contract as `app/hotels/config.ts` and
 * `app/restaurants/config.ts`.
 */

/**
 * Same-origin proxy path the BROWSER calls, e.g. `/bip-api/attractions`.
 *
 * Derived from the public URL's own pathname so it always matches the
 * `/${API_PREFIX}/attractions/:path*` rewrite in `next.config.js` — including
 * on the test environment, where the prefix is `bip-api-test`.
 */
function browserApiBase(): string {
  const publicUrl = process.env.NEXT_PUBLIC_ATTRACTIONS_API_URL ?? '';
  if (publicUrl.startsWith('/')) return publicUrl.replace(/\/+$/, '');
  try {
    const path = new URL(publicUrl).pathname.replace(/\/+$/, '');
    if (path && path !== '/') return path;
  } catch {
    /* Not an absolute URL — fall through to the documented default. */
  }
  return '/bip-api/attractions';
}

/**
 * Isomorphic base URL split.
 *
 * SSR calls the service directly via the internal URL. The BROWSER calls the
 * SAME-ORIGIN proxy path and lets `next.config.js` rewrite it to
 * `ATTRACTIONS_SERVICE_URL` — deliberately NOT the absolute
 * `NEXT_PUBLIC_ATTRACTIONS_API_URL`.
 *
 * ⚠️ This split is load-bearing, and getting it wrong produces a listing that
 * looks perfect until the first click. The public URL points at a DEPLOYED
 * host, while SSR renders against whatever the developer is running locally.
 * So the first paint came from the local service — filters, facets and counts
 * all correct — and then every filter click, sort change and scroll-append went
 * to the deployed host instead, which is a different build: it 400s on the
 * filter params it does not declare (`forbidNonWhitelisted`), or is simply
 * unreachable, and `fetchAttractions` swallows both into an empty result. The
 * grid emptied and the filters read as broken.
 *
 * Routing the browser through the proxy means both halves of the page always
 * talk to exactly one service, and it drops the cross-origin hop as well.
 */
export const ATTRACTIONS_API_BASE_URL =
  typeof window === 'undefined'
    ? (process.env.ATTRACTIONS_SERVICE_URL ?? process.env.NEXT_PUBLIC_ATTRACTIONS_API_URL ?? '')
    : browserApiBase();

/* ---------------------------------------------------------------------------
 * Domain types
 *
 * These mirror the normalised shapes the future `attractions-service` will
 * return (see the Bridgify plan): the frontend never sees a raw supplier
 * payload. When `@wl/api-client` gains an `AttractionsClient`, these move
 * there and this file re-exports them.
 * ------------------------------------------------------------------------- */

export interface AttractionSummary {
  id: string;
  slug: string;
  title: string;
  /** Short teaser — page `<meta name="description">`, which wants a hard cap. */
  summary: string;
  /**
   * Full supplier description, whitespace-normalised.
   *
   * The CARD renders this rather than `summary`: BCP-2057 §2 clamps the card to
   * two LINES with an ellipsis, and that is a width-dependent measurement only
   * CSS can make. Feeding it a pre-truncated string added a second "…" — and
   * put one on descriptions short enough that the AC says there must be none.
   */
  description: string;
  image: string;
  /** Human duration label, e.g. "2 hrs". */
  durationLabel: string;
  rating: number;
  reviewCount: number;
  /** Lowest ticket price, in `currency`. */
  fromPrice: number;
  currency: string;
  city: string;
  country: string;
  countrySlug: string;
  citySlug: string;
  /** Facet values — drive the sidebar filters. */
  types: string[];
  features: string[];
  /** `null` when the supplier gave no duration — it then belongs to no bucket. */
  durationBucket: DurationBucket | null;
  startTimeBuckets: StartTimeBucket[];
}

export type DurationBucket = 'upto1h' | '1to4h' | '4hto1d' | '1day' | '1dayPlus';
export type StartTimeBucket = 'morning' | 'afternoon' | 'evening';

export interface OpeningHours {
  label: string;
  value: string;
}

export interface AttractionFaq {
  question: string;
  answer: string;
}

/** Icon slot for a supplier-labelled field. The component maps it to an SVG. */
export type AttractionFieldIcon = 'clock' | 'ticket' | 'pin' | 'info' | 'headphones' | 'mic' | 'bus';

/**
 * One chip in the quick-facts strip (BCP-2065 §3).
 *
 * `label` is the SUPPLIER's name for the field and is rendered verbatim: a
 * product whose API says "Departure Point" must not read "Meeting Point". That
 * is why this is a label/value pair rather than four named properties — a named
 * property would force a hardcoded caption at the render site, which is exactly
 * the failure the ticket calls out.
 */
export interface AttractionInfoField {
  key: string;
  label: string;
  value: string;
  icon: AttractionFieldIcon;
  /** Present only on a field naming a place — drives the directions link. */
  mapsQuery?: string;
}

/** One block of the Schedule section — a day/hours grid, prose, or both. */
export interface AttractionScheduleBlock {
  key: string;
  label: string;
  icon: AttractionFieldIcon;
  rows: OpeningHours[];
  lines: string[];
}

/** One itinerary stop. The supplier sends prose, not a stop name. */
export interface AttractionItineraryStop {
  description: string;
  durationLabel: string | null;
  admissionIncluded: boolean | null;
}

export interface AttractionItinerary {
  start: string | null;
  stops: AttractionItineraryStop[];
  end: string | null;
}

export interface AttractionReview {
  id: string;
  author: string;
  /** ISO timestamp as supplied; `null` when the supplier gave no date. */
  date: string | null;
  rating: number | null;
  title: string | null;
  text: string;
}

/**
 * Review preview length before "Read more" (BCP-2066 §5.1).
 *
 * Truncation happens in the component rather than the mapper: the full text
 * has to reach the browser for Read more to expand without a second request.
 */
export const REVIEW_PREVIEW_CHARS = 250;

/**
 * Reviews per page — the "3 displayed review boxes" of BCP-2066 §5 (product
 * decision; the story also says 5 in two places). Bridgify sends at most 3
 * reviews per product, so pagination appears only from a 4th review.
 */
export const REVIEWS_PER_PAGE = 3;

/** FAQs per page (BCP-2066 §3.7). */
export const FAQS_PER_PAGE = 10;

/**
 * Truncate at a word boundary, never mid-word.
 *
 * Returns `null` when the text is within the cap, which is what lets the
 * component decide between "render plainly, no button" and "render a preview
 * with Read more" from one call — the AC distinguishes the two explicitly.
 */
export function reviewPreview(text: string, max = REVIEW_PREVIEW_CHARS): string | null {
  if (text.length <= max) return null;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice).trimEnd()}…`;
}

/**
 * A maps link for a place named by the API (BCP-2065 §3, BCP-2066 §4).
 *
 * A search query rather than a coordinate pin: the value we hold is a postal
 * address string, and Google resolves those far better than a lat/lon we would
 * have to geocode ourselves. Opened in a new tab so the page is not lost.
 */
export function mapsUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** 1 → "1st", 2 → "2nd", 11 → "11th", 22 → "22nd". */
function ordinal(n: number): string {
  const teen = n % 100;
  if (teen >= 11 && teen <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}

/**
 * Review date for display — "14th February", the Shows detail page's review
 * format (day and month, no year).
 *
 * Returns `null` for a missing or unparseable date so the caller omits the
 * element instead of rendering "Invalid Date". Formatted in UTC for the same
 * reason `parseIsoDate` is: a locale-local render differs between the server
 * and the browser and aborts hydration.
 */
export function formatReviewDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const month = new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'UTC' }).format(d);
  return `${ordinal(d.getUTCDate())} ${month}`;
}

export interface TicketOption {
  id: string;
  name: string;
  description?: string;
  price: number;
}

export interface TicketSlot {
  time: string;
  price: number;
  available: boolean;
}

export interface TicketTier {
  id: string;
  name: string;
  /** e.g. "Age 16+", "Upto 2 yrs". */
  ageLabel: string;
  price: number;
  min: number;
  max: number;
  defaultQty: number;
}

export interface AttractionDetail extends AttractionSummary {
  /**
   * Every image, in display order, with the main photo first.
   *
   * EMPTY IS A REAL STATE and must stay one: BCP-2065 §2.2 hides the whole
   * gallery section when a product has no images, so a placeholder must never
   * be substituted here — doing so renders a "photo" of nothing instead.
   */
  gallery: string[];
  /**
   * Quick-facts strip, as supplier-labelled pairs (BCP-2065 §3.1).
   *
   * Replaces the former `duration` / `startTime` / `meetingPoint` / `eTicket`
   * quartet. Those forced a hardcoded caption per chip, which silently broke
   * the ticket's central rule the moment a supplier named a field differently.
   */
  infoFields: AttractionInfoField[];
  /**
   * The supplier's pickup instructions. Kept OUT of `infoFields`: it is a
   * paragraph of instructions rather than a short fact, so it renders as its
   * own tab section instead of a chip.
   */
  pickupPoint: AttractionInfoField | null;
  overview: string[];
  highlights: string[];
  included: string[];
  notIncluded: string[];
  /** Schedule section blocks — opening hours, venue information, etc. */
  scheduleBlocks: AttractionScheduleBlock[];
  additionalInformation: string[];
  cancellationPolicy: string[];
  faqs: AttractionFaq[];
  reviews: AttractionReview[];
  /** Supplier's own flag — drives the Buy Tickets card's policy line. */
  freeCancellation: boolean;
  /** BCP-2066 §6 "Instructions" (HotelBeds arrival guidance). */
  instructions: string[];
  /** BCP-2066 §6 "Where You Will Go" landmark names (HotelBeds routes). */
  landmarks: string[];
  /** BCP-2066 §6 "Itinerary" (Viator stops). Null when there are no stops. */
  itinerary: AttractionItinerary | null;
  locationAddress: string;
  latitude: number;
  longitude: number;
  ticketOptions: TicketOption[];
  slots: TicketSlot[];
  tiers: TicketTier[];
}

/* ---------------------------------------------------------------------------
 * Formatting helpers
 * ------------------------------------------------------------------------- */

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  INR: '₹',
  AED: 'د.إ',
};

export function currencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? `${code} `;
}

/**
 * Money for display. Whole amounts drop the decimals ("From £45" in the
 * designs) while fractional amounts keep two ("£25.38").
 *
 * @deprecated Do NOT use for rendering a price. It formats in the SUPPLIER's
 * currency and cannot convert, so a visitor who picks a currency in the navbar
 * still sees the supplier's. Every price surface now goes through
 * `useCurrency().formatPrice(amount, supplierCurrency)` — the one mechanism
 * Hotels/Sports/Concerts share (see `_components/Money.tsx` for server pages).
 */
export function formatMoney(amount: number, currency = 'GBP'): string {
  const symbol = currencySymbol(currency);
  const isWhole = Number.isInteger(amount);
  return `${symbol}${amount.toFixed(isWhole ? 0 : 2)}`;
}

/**
 * Star icons for a rating, or `null` when the whole Ratings & Reviews block
 * must be hidden.
 *
 * Follows the Shows card (`NearbyShowCard.getStarValue`, also in
 * `concerts/NearbyShows.tsx`) so a rating reads the same across modules:
 *
 *   4.8+ → 5 · 4.3–4.7 → 4½ · 4.0–4.2 → 4   (the BCP-2057 §3 bands)
 *   below 4.0 → whole part, plus a half star for ANY decimal (3.5 → 3½,
 *   3.1 → 3½, 3.0 → 3)
 *
 * The rule below 4.0 departs from BCP-2057 §3, which rounds down to whole
 * stars there. Product chose the Shows behaviour.
 *
 * `null` covers "no rating" and anything below 1, as the story requires: an
 * unrated product shows no stars, no "0 reviews" and no placeholder, rather
 * than five empty stars.
 */
export function starsForRating(
  rating: number | null | undefined,
): { full: number; half: boolean } | null {
  if (rating == null || Number.isNaN(rating) || rating < 1) return null;
  if (rating >= 4.8) return { full: 5, half: false };
  if (rating >= 4.3) return { full: 4, half: true };
  if (rating >= 4.0) return { full: 4, half: false };
  return { full: Math.floor(rating), half: rating % 1 !== 0 };
}

/** Always two decimals — used for totals, where "£50.76" must not become "£51". */
export function formatMoneyExact(amount: number, currency = 'GBP'): string {
  return `${currencySymbol(currency)}${amount.toFixed(2)}`;
}

/**
 * Detail/booking URL for an attraction. SEO breadcrumb segments come first and
 * the slug is always last, so the slug alone is enough to resolve the record —
 * same contract as `buildRestaurantUrl` in `app/restaurants/config.ts`.
 */
export function buildAttractionUrl(
  a: Pick<AttractionSummary, 'slug' | 'citySlug' | 'countrySlug'>,
): string {
  return `/attractions-experiences/${a.countrySlug}/${a.citySlug}/${a.slug}`;
}

/** "Thursday 01 Jan 2026, 07:30 PM" — the Order Summary date format. */
export function formatSummaryDateTime(isoDate: string, time?: string): string {
  const d = parseIsoDate(isoDate);
  if (!d) return time ?? '';
  const datePart = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
  return time ? `${datePart}, ${time}` : datePart;
}

/** "Monday, Aug 3, 2026" — the confirmation-page date format. */
export function formatLongDate(isoDate: string): string {
  const d = parseIsoDate(isoDate);
  if (!d) return isoDate;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/** "01 Jan 2026". */
export function formatShortDate(isoDate: string): string {
  const d = parseIsoDate(isoDate);
  if (!d) return isoDate;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Parse `YYYY-MM-DD` as UTC midnight. Deliberately not `new Date(string)` with
 * a local-time fallback: a locally-parsed date shifts a day either side of the
 * timezone boundary, which would render a different date on the server than in
 * the browser and abort hydration.
 */
export function parseIsoDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `Date` → `YYYY-MM-DD` (UTC), the inverse of `parseIsoDate`. */
export function toIsoDate(d: Date): string {
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Read one search param that may arrive as `string | string[] | undefined`.
 * Mirrors `spParam` in `app/hotels/config.ts`.
 */
export function spParam(raw: string | string[] | undefined): string | undefined {
  if (Array.isArray(raw)) return raw[0];
  return raw;
}

/**
 * The destination in force, as every attractions surface passes it around.
 *
 * `cityId` is the identity and the only thing that filters; `city`, `citySlug`
 * and the country fields are display and routing. Keep them together — a
 * component holding the name without the id is exactly the state that made two
 * different Las Vegases into one listing.
 */
export interface SelectedDestination {
  /** Canonical Bridgify city id. `null` for destinations that have none. */
  cityId: number | null;
  city: string;
  citySlug?: string;
  /** Unique readable slug — the single location value the URL carries. */
  urlSlug?: string;
  /** Real region name, present only where one disambiguates a shared slug. */
  region?: string;
  country?: string;
  countrySlug?: string;
  /** How many products we hold. `0` means "real place, nothing to sell". */
  attractionCount?: number;
}

/**
 * A positive integer `?cityId=` from the URL, or undefined.
 *
 * Anything else — a name, a float, a negative, `0` — is REJECTED rather than
 * coerced. A bad id must degrade to "no destination chosen" so the caller can
 * resolve it properly; silently turning it into a different number would point
 * the listing at an arbitrary city.
 */
export function cityIdParam(raw: string | string[] | undefined): number | undefined {
  const value = spParam(raw)?.trim();
  if (!value || !/^\d+$/.test(value)) return undefined;
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/** Page `<title>`, bypassing the root `%s | BookItPlease` template. */
export function attractionsPageMeta(title: string, description?: string) {
  return {
    title: { absolute: `${title} | BookItPlease` },
    ...(description ? { description } : {}),
  };
}

/** SEO title + description for the listing — the same for every destination. */
export function listingPageMeta() {
  return {
    title: { absolute: 'Discover Tours, Attractions & Things to Do | Book It Please' },
    description:
      'Discover tours, attractions and experiences with Book It Please, from famous landmarks and museums to guided tours and family days out. Search and book online.',
  };
}

/**
 * SEO title + description for a detail page: "London Eye | Book It Please".
 * A product with no city drops "in <city>" rather than printing "in ".
 */
export function detailPageMeta(name: string, city?: string | null) {
  const where = city?.trim() ? `${name} in ${city.trim()}` : name;
  return {
    title: { absolute: `${name} | Book It Please` },
    description: `Book ${where} with Book It Please. Check availability, read reviews and secure your tickets online.`,
  };
}

/* ---------------------------------------------------------------------------
 * Filter facet definitions — shared by the sidebar UI and the filter predicate
 * so the two can never drift (the hotels domain has a documented bug from
 * exactly that drift: `DISTANCE_THRESHOLDS_MILES` vs `DISTANCE_OPTIONS`).
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * Sorting (BCP-2058 §2, BCP-2059/2060/2061/2067)
 * ------------------------------------------------------------------------- */

/**
 * The four sort options, in BCP-2058's own display order.
 *
 * `param` is what travels in the URL; `sortBy` / `sortOrder` are what the
 * service's DTO declares. They are translated EXPLICITLY here rather than by
 * forwarding search params wholesale — the global `ValidationPipe` runs
 * `forbidNonWhitelisted`, so a URL-only name reaching the service 400s the
 * request and the UI just shows an empty grid with nothing pointing at the
 * cause.
 *
 * The comparators themselves live server-side (`attractions.repository.ts`
 * `sortStage`), because the catalogue is ~10,000 products and a page of 20
 * cannot be sorted into the right order client-side.
 */
export const SORT_OPTIONS = [
  { param: 'recommended', label: 'Our Recommendations', sortBy: 'recommended' },
  { param: 'rating', label: 'Highest Rated', sortBy: 'rating' },
  { param: 'price-asc', label: 'Price: Low to High', sortBy: 'price', sortOrder: 'asc' },
  { param: 'price-desc', label: 'Price: High to Low', sortBy: 'price', sortOrder: 'desc' },
] as const satisfies readonly {
  param: string;
  label: string;
  sortBy: string;
  sortOrder?: string;
}[];

export type SortValue = (typeof SORT_OPTIONS)[number]['param'];

/**
 * The default order, and what Reset returns to (BCP-2067 §1). Four separate
 * tickets defer to "default recommendation order" without defining it; this is
 * it.
 */
export const DEFAULT_SORT: SortValue = 'recommended';

export function isSortValue(value: string | undefined): value is SortValue {
  return SORT_OPTIONS.some((o) => o.param === value);
}

export function sortOptionFor(value: SortValue) {
  return SORT_OPTIONS.find((o) => o.param === value) ?? SORT_OPTIONS[0];
}

/* ---------------------------------------------------------------------------
 * User-visible strings
 *
 * The tickets contradict each other on both of these, so each is driven from
 * ONE constant rather than typed at each call site.
 * ------------------------------------------------------------------------- */

/**
 * BCP-2059 writes "Sort by : X", BCP-2060/2061 write "Sorted by : X.", and
 * BCP-2067 lowercases the option name that BCP-2058 title-cases. One pattern,
 * with the option names spelled exactly as BCP-2058 §2 lists them.
 */
export function sortLabel(value: SortValue): string {
  return `Sort by : ${sortOptionFor(value).label}`;
}

/* ---------------------------------------------------------------------------
 * Refetch progress (mirrors the hotels listing's filter bar)
 * ------------------------------------------------------------------------- */

export interface SearchStage {
  label: string;
  /** How long this stage is shown, ms. Purely cosmetic pacing. */
  duration: number;
}

/**
 * Stages shown while the listing refetches after a filter, sort or search
 * change — the same two the hotels listing uses for the equivalent action, so
 * the two verticals read identically.
 *
 * These are PACING ONLY. The attractions listing is one REST call, not a
 * streamed search, so there is no real progress to report; the bar exists to
 * show that the request is in flight and is capped below 100% until the
 * response actually lands (see `AttractionsList`). It is deliberately not
 * presented as a percentage anywhere in the UI.
 */
export const FILTER_STAGES: SearchStage[] = [
  { label: 'Sorting results', duration: 700 },
  { label: 'Finalising results', duration: 700 },
];

/** Which stage `elapsed` ms falls in. */
export function stageIndexAt(stages: SearchStage[], elapsed: number): number {
  let acc = 0;
  for (let i = 0; i < stages.length; i += 1) {
    acc += stages[i].duration;
    if (elapsed < acc) return i;
  }
  return stages.length - 1;
}

/**
 * The listing's empty state.
 *
 * Product-specified wording, in the product's own title case, and it
 * deliberately overrides the tickets: the BCP-2059/2060/2061 drafts say "No
 * records found" and BCP-2063 "No Records Found." Both describe a database, not
 * a trip — "records" is our word, not the visitor's. This names what is
 * actually missing.
 */
export const NO_RECORDS_FOUND = 'No attractions or experiences available.';

/* ---------------------------------------------------------------------------
 * Filters (BCP-2062 / BCP-2063)
 * ------------------------------------------------------------------------- */

/**
 * Hints under the Start Time labels.
 *
 * The service supplies every option's label and count; only this sub-label is
 * presentational, so it stays here. The labels themselves are rendered verbatim
 * as the ticket writes them even though the implemented ranges are bounded and
 * mutually exclusive — see the service's `facet-taxonomy.ts`.
 */
export const START_TIME_HINTS: Record<string, string> = {
  morning: 'Before 12 PM',
  afternoon: 'After 12 PM',
  evening: 'After 5 PM',
};

/** One selectable value with its available result count, as the service emits it. */
export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

/** The filter panel's contents, derived from the current result set. */
export interface ListingFacets {
  types: FacetOption[];
  features: FacetOption[];
  /** Value = ISO code (`es`), label = its name ("Spanish") — from the service. */
  languages: FacetOption[];
  durations: FacetOption[];
  startTimes: FacetOption[];
  guestRatings: FacetOption[];
  /**
   * Slider bounds AND the currency they are denominated in (the catalogue's
   * dominant currency — attractions prices are stored per supplier and never
   * normalised). The panel converts these into the visitor's selected currency
   * for display and back again before filtering, the same round-trip the Shows
   * price slider does with GBP.
   */
  priceRange: { min: number; max: number; currency: string } | null;
}

export const EMPTY_FACETS: ListingFacets = {
  types: [],
  features: [],
  languages: [],
  durations: [],
  startTimes: [],
  guestRatings: [],
  priceRange: null,
};

/** The multi-select categories, keyed as they appear in the URL and the DTO. */
export const FILTER_CATEGORIES = [
  { key: 'types', title: 'Category' },
  { key: 'features', title: 'Features' },
  { key: 'languages', title: 'Language' },
  { key: 'durations', title: 'Duration' },
  { key: 'startTimes', title: 'Start Time' },
  { key: 'guestRatings', title: 'Guest Rating' },
] as const;

export type FilterCategory = (typeof FILTER_CATEGORIES)[number]['key'];

export function categoryTitle(key: FilterCategory): string {
  return FILTER_CATEGORIES.find((c) => c.key === key)?.title ?? key;
}

/**
 * The complete listing state (BCP-2063 §7 reference shape).
 *
 * Price is NULLABLE on both ends, and that is load-bearing: "no price filter"
 * has to be distinguishable from "£0 to the highest price we know of". The
 * previous client-side filter defaulted `maxPrice` to a hardcoded 600, which
 * silently hid the 6% of the live catalogue that costs more — with no filter
 * applied, and with the result count disagreeing with the service's own total.
 */
export interface AttractionFilters {
  minPrice: number | null;
  maxPrice: number | null;
  types: string[];
  features: string[];
  languages: string[];
  durations: string[];
  startTimes: string[];
  guestRatings: number[];
}

export function emptyFilters(): AttractionFilters {
  return {
    minPrice: null,
    maxPrice: null,
    types: [],
    features: [],
    languages: [],
    durations: [],
    startTimes: [],
    guestRatings: [],
  };
}

export function hasActiveFilters(f: AttractionFilters): boolean {
  return (
    f.minPrice !== null ||
    f.maxPrice !== null ||
    f.types.length > 0 ||
    f.features.length > 0 ||
    f.languages.length > 0 ||
    f.durations.length > 0 ||
    f.startTimes.length > 0 ||
    f.guestRatings.length > 0
  );
}

/** Values selected in one category, as strings (the shape the panel compares). */
export function selectedIn(f: AttractionFilters, key: FilterCategory): string[] {
  return key === 'guestRatings' ? f.guestRatings.map(String) : f[key];
}

/** Replace one category's selection wholesale — used by both the inline
 *  checkboxes and the More popup's Apply, so the two surfaces write through the
 *  same door (BCP-2063 §8). */
export function withCategory(
  f: AttractionFilters,
  key: FilterCategory,
  values: string[],
): AttractionFilters {
  if (key === 'guestRatings') {
    return { ...f, guestRatings: values.map(Number).filter((n) => Number.isFinite(n)) };
  }
  return { ...f, [key]: values };
}

export function toggleInCategory(
  f: AttractionFilters,
  key: FilterCategory,
  value: string,
): AttractionFilters {
  const current = selectedIn(f, key);
  const next = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  return withCategory(f, key, next);
}

/* ---------------------------------------------------------------------------
 * Chips (BCP-2063 §7)
 * ------------------------------------------------------------------------- */

export interface FilterChip {
  /** Stable React key. */
  id: string;
  label: string;
  category: FilterCategory | 'price';
  /** The value to remove; absent for the single price chip. */
  value?: string;
}

/**
 * Chips are DERIVED from filter state on every render and never stored
 * separately — that is precisely what guarantees BCP-2063 §7's "chips and
 * checkbox states stay in sync at all times". A second copy of the selection
 * would be a second source of truth, and the two would drift the first time a
 * popup Apply and an inline toggle raced.
 *
 * Labels come from the facet options so a chip reads the way its checkbox does
 * ("4.0+", not "4"); a value whose option has since dropped out of the result
 * set falls back to the raw value rather than disappearing, so it stays
 * removable.
 */
export function activeChips(
  f: AttractionFilters,
  facets: ListingFacets,
  /**
   * Renders one price-chip bound. Supplied by `FilterChips`, which has the
   * currency context; it converts the stored catalogue-currency number into the
   * visitor's selected currency and adds the symbol. Omitted (tests, or any
   * caller outside the provider) → the bare rounded number, as before.
   */
  formatBound?: (amount: number, currency: string) => string,
): FilterChip[] {
  const chips: FilterChip[] = [];

  for (const { key } of FILTER_CATEGORIES) {
    const options = facets[key];
    for (const value of selectedIn(f, key)) {
      chips.push({
        id: `${key}:${value}`,
        label: options.find((o) => o.value === value)?.label ?? value,
        category: key,
        value,
      });
    }
  }

  // ONE chip for the price range, not two — it is a single range control, and a
  // pair of chips would imply the two bounds could be cleared independently.
  if (f.minPrice !== null || f.maxPrice !== null) {
    chips.push({
      id: 'price',
      label: priceChipLabel(f, facets, formatBound),
      category: 'price',
    });
  }

  return chips;
}

function priceChipLabel(
  f: AttractionFilters,
  facets: ListingFacets,
  formatBound?: (amount: number, currency: string) => string,
): string {
  const min = f.minPrice ?? facets.priceRange?.min ?? 0;
  const max = f.maxPrice ?? facets.priceRange?.max;
  // Filter values are stored in the catalogue currency (that is what the API
  // compares against); the chip shows them in the visitor's currency.
  const base = facets.priceRange?.currency ?? 'USD';
  const show = (n: number) => (formatBound ? formatBound(n, base) : String(Math.round(n)));
  return max === undefined ? `From ${show(min)}` : `${show(min)} – ${show(max)}`;
}

export function removeChip(f: AttractionFilters, chip: FilterChip): AttractionFilters {
  if (chip.category === 'price') return { ...f, minPrice: null, maxPrice: null };
  return toggleInCategory(f, chip.category, chip.value ?? '');
}

/* ---------------------------------------------------------------------------
 * URL <-> state
 *
 * Search state lives in the URL, not in React state: the server component
 * re-reads it on every render, so back-navigation and a shared link restore the
 * exact same listing. This is a standing rule for every listing in the app.
 * ------------------------------------------------------------------------- */

type ParamBag = { getAll(key: string): string[]; get(key: string): string | null };

/**
 * Next's `searchParams` record → a `URLSearchParams`.
 *
 * A repeated param reaches a server component as an array and a single one as a
 * string; going through `URLSearchParams` means the page and the browser read
 * the URL through exactly the same code, so a filter cannot survive a reload
 * differently from how it survives a click.
 */
export function toParamBag(
  raw: Record<string, string | string[] | undefined>,
): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(raw)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) value.forEach((v) => sp.append(key, v));
    else sp.append(key, value);
  }
  return sp;
}

function readList(sp: ParamBag, key: string): string[] {
  /*
   * Repeated params only (`?types=a&types=b`) — values are NEVER split on
   * commas.
   *
   * Bridgify category labels contain commas ("Museums, Culture & History"), so
   * splitting turned one selection into two values matching nothing: the
   * largest option in the panel returned 7 results instead of 174, and the
   * checkbox appeared to un-tick itself on reload because the value read back
   * never matched the one written out.
   */
  return sp
    .getAll(key)
    .map((v) => v.trim())
    .filter(Boolean);
}

function readNumber(sp: ParamBag, key: string): number | null {
  const raw = sp.get(key);
  if (raw === null || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function filtersFromParams(sp: ParamBag): AttractionFilters {
  return {
    minPrice: readNumber(sp, 'minPrice'),
    maxPrice: readNumber(sp, 'maxPrice'),
    types: readList(sp, 'types'),
    features: readList(sp, 'features'),
    languages: readList(sp, 'languages'),
    durations: readList(sp, 'durations'),
    startTimes: readList(sp, 'startTimes'),
    guestRatings: readList(sp, 'guestRatings')
      .map(Number)
      .filter((n) => Number.isFinite(n)),
  };
}

export function sortFromParams(sp: ParamBag): SortValue {
  const raw = sp.get('sort') ?? undefined;
  return isSortValue(raw) ? raw : DEFAULT_SORT;
}

/**
 * State → query string, for `router.replace`.
 *
 * Defaults are OMITTED rather than written out: a pristine listing keeps a
 * clean `?city=London` URL, and "no sort chosen" and "Our Recommendations
 * chosen" produce the same link, which is what makes them the same thing.
 */
export function listingQuery(params: {
  /**
   * The READABLE destination value — a slug like `london`, or a display name
   * for a destination that has no slug.
   *
   * This is the ONLY location value that reaches the address bar. The canonical
   * `cityId` is deliberately absent: it is an internal Bridgify identifier, it
   * means nothing to a person reading the link, and exposing it alongside a
   * slug and a display name gave one destination three competing
   * representations in one URL. The server resolves this value back to its
   * `cityId` on every render — see `findCityBySlugOrName`.
   */
  citySlug?: string;
  query?: string;
  filters: AttractionFilters;
  sort: SortValue;
  view?: 'grid' | 'list';
}): string {
  const sp = new URLSearchParams();
  // ONE destination parameter, and it is the readable one. `?city=london`,
  // never `?cityId=2643743&citySlug=london&city=London`.
  if (params.citySlug) sp.set('city', params.citySlug);
  if (params.query) sp.set('q', params.query);

  for (const { key } of FILTER_CATEGORIES) {
    for (const value of selectedIn(params.filters, key)) sp.append(key, value);
  }
  if (params.filters.minPrice !== null) sp.set('minPrice', String(params.filters.minPrice));
  if (params.filters.maxPrice !== null) sp.set('maxPrice', String(params.filters.maxPrice));

  if (params.sort !== DEFAULT_SORT) sp.set('sort', params.sort);
  // Grid is the default view, so only List is worth recording.
  if (params.view === 'list') sp.set('view', 'list');

  return sp.toString();
}
